<script lang="ts">
	/**
	 * `/Products/:familySlug` — the tier comparison page (Design §14). Public.
	 *
	 * The table renders the server's `action` discriminator; this page only decides where a click
	 * goes, and dresses the line the tiers belong to. Buy itself lives on the product page, which
	 * carries the coupon field, the seat control and the terms.
	 *
	 * The line's own logo and description come from a **second, optional** read. `listCatalogProducts`
	 * is the page — without it there are no tiers to compare — while `listFamilies` supplies only
	 * the masthead, so a failure there is swallowed rather than turned into an error state. A
	 * comparison table refusing to render because a description could not be fetched would be a
	 * decoration taking the page down with it.
	 */

	import { Breadcrumb, Button, Toggle } from 'flowbite-svelte';
	import { push } from '../../svelte-spa-router';
	import {
		CurrencySelector,
		EmptyState,
		PageHeader,
		PlanComparisonTable,
		RoutedBreadcrumbItem,
		RoutedLink,
		SkeletonWrapper,
		ComparisonSkeleton
	} from '../../components';
	import { listCatalogProducts, listFamilies } from '../../AppNetwork';
	import { configurationStore } from '../../AppConfiguration';
	import { setAnalyticsContext, track } from '../../stores/analytics';
	import {
		activeCurrency,
		activeCurrencyCode,
		availableCurrencies,
		pinnedCurrency,
		selectCurrency
	} from '../../stores/storefront';
	import { isAuthorized } from '../../AbstractID';
	import { formatMoney, mediaURL } from '../../Utilities';
	import type { Price, Product, ProductFamily } from '../../AppTypes';

	/** `svelte-spa-router` passes the matched route parameters in. */
	export let params: { familySlug?: string } = {};

	let products: Product[] = [];
	let family: ProductFamily | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	let notFound: boolean = false;

	$: familySlug = params.familySlug ?? '';
	/*
	 * §12.2. Keyed on the slug rather than fired from `onMount`, because the router reuses this
	 * component when one line's comparison links to another's — an `onMount` there would record the
	 * first line and none of the rest.
	 */
	$: if (familySlug.length > 0) {
		// The line this page is about, so a currency change or a refusal raised here is attributable
		// to it rather than landing in the breakdowns as "Unknown".
		setAnalyticsContext({ familySlug });
		track('family_view', { path: `/Products/${familySlug}`, familySlug });
	}
	$: isOpen = $configurationStore?.isOpen ?? false;
	$: familyName = family?.name ?? products[0]?.familyName ?? familySlug;

	/** The tier the line promotes, if it names one — the page's one recommendation. */
	$: promoted = products.find(product => typeof product.highlight === 'string' && product.highlight.length > 0) ?? null;

	/** Off by default: the shared rows are the reassurance that the entry tier is not a stub. */
	let differencesOnly: boolean = false;

	function headline(product: Product): Price | null {
		return product.prices.find(price => price.isDefault) ?? product.prices[0] ?? null;
	}

	/**
	 * What the ladder costs, end to end.
	 *
	 * The single most useful sentence on a comparison page, and it was nowhere on it: a customer
	 * arriving from a shared link had to read every column to learn whether this line was a nine
	 * dollar thing or a five hundred dollar thing. Derived from the amounts already on the page,
	 * so it cannot disagree with the table below it, and **absent** rather than guessed when a tier
	 * is unpriced in this currency — §10 forbids quoting anybody in money they did not ask for.
	 */
	$: ladderAmounts = products
		.map(product => headline(product)?.amount ?? null)
		.filter((amount): amount is number => amount !== null);
	$: priceRange =
		$activeCurrency === undefined || ladderAmounts.length < 2 || ladderAmounts.length !== products.length
			? null
			: {
					low: formatMoney(Math.min(...ladderAmounts), $activeCurrency),
					high: formatMoney(Math.max(...ladderAmounts), $activeCurrency)
			  };

	async function fnLoad(slug: string, currencyCode: string): Promise<void> {
		if (slug.length === 0) return;

		loading = true;
		loadError = '';
		notFound = false;
		try {
			products = (
				await listCatalogProducts({ familySlug: slug, currency: currencyCode || undefined, limit: 100 })
			).items;
			notFound = products.length === 0;
		} catch (error) {
			// A 404 here means the family slug is wrong, which is a different thing to say than
			// "the catalogue is down" — the visitor most likely followed a stale link.
			const message = (error as Error).message;
			if (/not found/i.test(message)) {
				notFound = true;
			} else {
				loadError = message;
			}
		} finally {
			loading = false;
		}

		void fnLoadFamily(slug, currencyCode);
	}

	/** The masthead, and only the masthead. A failure here costs a paragraph, not the page. */
	async function fnLoadFamily(slug: string, currencyCode: string): Promise<void> {
		try {
			const result = await listFamilies({ currency: currencyCode || undefined });
			family = result.items.find(candidate => candidate.slug === slug) ?? null;
		} catch {
			family = null;
		}
	}

	$: void fnLoad(familySlug, $activeCurrencyCode);
</script>

<Breadcrumb class="mb-4">
	<RoutedBreadcrumbItem href="/" home>Store</RoutedBreadcrumbItem>
	<!-- The last crumb carries no href, so it renders muted. -->
	<RoutedBreadcrumbItem>{familyName}</RoutedBreadcrumbItem>
</Breadcrumb>

<!--
	The line's masthead. The logo sits beside the heading rather than above it so the name keeps
	the `PageHeader` underline that every other page's title has — a heading that loses its rule
	because it gained an image reads as a different kind of page.
-->
<div class="flex items-start gap-4">
	{#if family?.imageURL}
		<img src={mediaURL(family.imageURL)} alt="" class="w-14 h-14 rounded-2xl object-contain shrink-0 mt-1" />
	{/if}
	<div class="min-w-0 grow">
		<PageHeader title={familyName}>
			<svelte:fragment slot="description">
				{#if family?.description}
					{family.description}
				{:else}
					Every tier side by side. Your current plan is labelled, and moving between tiers keeps
					what you have already paid for.
				{/if}
			</svelte:fragment>
		</PageHeader>
	</div>
</div>

<!--
	The shape of the ladder, before the grid of it.
	
	A comparison page that opens on a table of ticks asks the customer to derive the two facts they
	actually arrived for — how many rungs there are, and what the line costs end to end — by reading
	every column. Both are already on the page as data; stating them is free, and it is what turns a
	reference document into a decision aid.
-->
{#if products.length > 0}
	<div class="flex flex-wrap items-center gap-x-6 gap-y-2 mb-5">
		<p class="text-lg">
			<!-- No newline between the count and the comma, or the markup renders "3 tiers , from". -->
			<strong class="font-bold">{products.length} {products.length === 1 ? 'tier' : 'tiers'}</strong>{#if priceRange !== null}<span
					class="text-default"
					>, from <span class="tabular-nums font-semibold text-gray-900 dark:text-white">{priceRange.low}</span>
					to <span class="tabular-nums font-semibold text-gray-900 dark:text-white">{priceRange.high}</span></span
				>{/if}
		</p>
		{#if promoted !== null}
			<!-- The line's own recommendation, said once and in its own words. -->
			<p class="text-sm">
				<i class="fa-solid fa-star mr-1.5 text-primary-500" /><span class="text-default">{promoted.highlight}:</span>
				<RoutedLink to={`/Product/${promoted.slug}`} class="font-semibold text-primary-500 hover:underline">
					{promoted.name}
				</RoutedLink>
			</p>
		{/if}
	</div>
{/if}

<div class="controlpanel flex flex-wrap items-center gap-4 mb-6">
	<CurrencySelector
		currencies={$availableCurrencies}
		value={$activeCurrencyCode}
		pinnedCurrency={$pinnedCurrency}
		compact
		on:change={event => selectCurrency(event.detail)}
	/>
	{#if products.length > 1}
		<!--
			The table's job is the difference. A row every tier answers identically is not one — it
			is space that pushed the real difference below the fold. Opt-in, because those shared
			rows are also the reassurance that the entry tier is not a stub.
		-->
		<Toggle bind:checked={differencesOnly} class="text-sm">Only what differs</Toggle>
	{/if}
	{#if !isOpen}
		<span class="text-sm text-default"><i class="fa-solid fa-circle-info mr-2" />Not open for purchases yet.</span>
	{/if}
</div>

{#if loading}
	<!-- A skeleton the shape of the table, so a fast load does not swap a spinner for a grid. -->
	<SkeletonWrapper>
		<ComparisonSkeleton />
	</SkeletonWrapper>
{:else if notFound}
	<EmptyState
		title="No such product family."
		description="The link may be out of date. The full catalogue lists everything on sale."
	>
		<Button color="alternative" size="sm" on:click={() => push('/')}>Browse the store</Button>
	</EmptyState>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The tiers could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => fnLoad(familySlug, $activeCurrencyCode)}>
			Try again
		</Button>
	</EmptyState>
{:else}
	<PlanComparisonTable
		{products}
		currency={$activeCurrency}
		{isOpen}
		{differencesOnly}
		isSignedIn={$isAuthorized}
		on:select={event => push(`/Product/${event.detail.product.slug}`)}
	/>

	<!--
		The legend earns its place the moment a row carries an amount rather than a tick: a dash and
		a blank cell are not the same thing, and only one of them is on the screen.
	-->
	<p class="text-xs text-default mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
		<span><i class="fa-solid fa-check text-green-600 dark:text-green-400 mr-1.5" />Included</span>
		<span><span class="mr-1.5">—</span>Not included</span>
		<span>Every figure is priced in {($activeCurrency?.code ?? '').toUpperCase()}; tax is added at checkout where it applies.</span>
	</p>
{/if}
